import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRight, ShieldCheck, CalendarCheck, CheckCircle2,
    Clock, Users, Sparkles, MessageSquare, Globe2
} from "lucide-react";
import { LoginModal } from "../auth/components/LoginModal";
import { RegisterModal } from "../auth/components/RegisterModal";
import { LandingNav } from "./components/LandingNav";
import { Logo } from "../../components/common/Logo";
import { CookieSettingsButton } from "../../components/gdpr";
import { demoRequestsApi } from "../../lib/api/demoRequests";

interface FormState {
    name: string;
    email: string;
    company: string;
    role: string;
    teamSize: string;
    monthlyRevenue: string;
    channels: string[];
    message: string;
    slot: string;
}

const INITIAL: FormState = {
    name: "",
    email: "",
    company: "",
    role: "",
    teamSize: "",
    monthlyRevenue: "",
    channels: [],
    message: "",
    slot: "",
};

const TEAM_SIZE_OPTIONS = ["1", "2-5", "6-20", "21-50", "50+"];
const REVENUE_OPTIONS = ["under_10k", "10k_50k", "50k_250k", "250k_1m", "over_1m"];
const CHANNEL_OPTIONS = ["shopify", "woocommerce", "meta", "tiktok", "google", "amazon"];

const buildSlots = () => {
    const slots: { date: string; label: string; sub: string }[] = [];
    const d = new Date();
    let count = 0;
    while (count < 5) {
        d.setDate(d.getDate() + 1);
        const day = d.getDay();
        if (day === 0 || day === 6) continue; // weekdays only
        slots.push({
            date: d.toISOString().slice(0, 10),
            label: d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }),
            sub: "10:00 · 14:00 · 16:30",
        });
        count++;
    }
    return slots;
};

const DemoPage = () => {
    const { t } = useTranslation("common");
    const [searchParams, setSearchParams] = useSearchParams();
    const activeModal = searchParams.get("modal");
    const openLogin = () => setSearchParams({ modal: "login" });
    const openRegister = () => setSearchParams({ modal: "register" });
    const closeModal = () => setSearchParams({});

    const [form, setForm] = useState<FormState>(INITIAL);
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const slots = useMemo(buildSlots, []);

    const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
        setForm((prev) => ({ ...prev, [key]: value }));

    const toggleChannel = (channel: string) => {
        setForm((prev) => ({
            ...prev,
            channels: prev.channels.includes(channel)
                ? prev.channels.filter((c) => c !== channel)
                : [...prev.channels, channel],
        }));
    };

    const canSubmit =
        form.name.trim().length > 1 &&
        /^\S+@\S+\.\S+$/.test(form.email) &&
        form.company.trim().length > 0 &&
        form.slot.length > 0;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit || isSubmitting) return;
        setIsSubmitting(true);
        setSubmitError(null);
        try {
            await demoRequestsApi.submit({
                name: form.name,
                email: form.email,
                company: form.company,
                role: form.role || undefined,
                teamSize: form.teamSize || undefined,
                monthlyRevenue: form.monthlyRevenue || undefined,
                channels: form.channels.length ? form.channels : undefined,
                slot: form.slot || undefined,
                message: form.message || undefined,
            });
            setSubmitted(true);
        } catch (err) {
            const message =
                (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
                t("demo.form.errorGeneric");
            setSubmitError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-background-page font-sans selection:bg-accent-primary/20 selection:text-text-primary">
            <LoginModal isOpen={activeModal === "login"} onClose={closeModal} />
            <RegisterModal
                isOpen={activeModal === "register"}
                onClose={closeModal}
                prefillEmail={searchParams.get("email") || undefined}
            />

            <LandingNav onLogin={openLogin} onRegister={openRegister} />

            <main className="pt-28 sm:pt-32 pb-20">
                {/* Hero */}
                <section className="relative">
                    <div
                        aria-hidden
                        className="pointer-events-none absolute inset-0 opacity-[0.3] dark:opacity-[0.2]"
                        style={{
                            backgroundImage:
                                "linear-gradient(to right, rgba(120,120,120,0.12) 1px, transparent 1px), linear-gradient(to bottom, rgba(120,120,120,0.12) 1px, transparent 1px)",
                            backgroundSize: "56px 56px",
                            maskImage:
                                "radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 75%)",
                            WebkitMaskImage:
                                "radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 75%)",
                        }}
                    />
                    <div className="relative container mx-auto px-4 sm:px-6">
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="max-w-3xl mx-auto text-center mb-10 sm:mb-14"
                        >
                            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-neutral-200 dark:border-neutral-700 bg-white/70 dark:bg-neutral-900/60 text-text-primary text-xs font-medium mb-6">
                                <CalendarCheck size={13} className="text-accent-primary" />
                                {t("demo.heroBadge")}
                            </span>
                            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold font-display text-text-primary tracking-tight leading-[1.05] mb-5">
                                {t("demo.heroTitle")}
                            </h1>
                            <p className="text-lg text-neutral-600 dark:text-neutral-300 max-w-2xl mx-auto">
                                {t("demo.heroSubtitle")}
                            </p>
                        </motion.div>

                        {/* Two-column: form + benefits */}
                        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 max-w-6xl mx-auto">
                            {/* Form */}
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.05 }}
                                className="lg:col-span-3 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl shadow-sm p-6 sm:p-8"
                            >
                                {submitted ? (
                                    <div className="py-12 text-center flex flex-col items-center">
                                        <div className="w-16 h-16 rounded-2xl bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-500 flex items-center justify-center mb-5">
                                            <CheckCircle2 size={32} />
                                        </div>
                                        <h2 className="text-2xl font-semibold font-display text-text-primary mb-2">
                                            {t("demo.success.title")}
                                        </h2>
                                        <p className="text-neutral-600 dark:text-neutral-400 max-w-md leading-relaxed">
                                            {t("demo.success.body")}
                                        </p>
                                        <button
                                            onClick={() => { setSubmitted(false); setForm(INITIAL); }}
                                            className="mt-6 text-sm font-medium text-accent-primary hover:underline"
                                        >
                                            {t("demo.success.bookAnother")}
                                        </button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="space-y-5">
                                        <h2 className="text-xl font-semibold font-display text-text-primary mb-1">
                                            {t("demo.form.title")}
                                        </h2>
                                        <p className="text-sm text-neutral-500 dark:text-neutral-400 -mt-2">
                                            {t("demo.form.subtitle")}
                                        </p>

                                        <div className="grid sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-300 mb-1.5">{t("demo.form.name")}</label>
                                                <input
                                                    type="text"
                                                    value={form.name}
                                                    onChange={(e) => set("name", e.target.value)}
                                                    placeholder={t("demo.form.namePlaceholder") as string}
                                                    className="w-full px-3 py-2.5 text-sm border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary transition-colors"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-300 mb-1.5">{t("demo.form.email")}</label>
                                                <input
                                                    type="email"
                                                    value={form.email}
                                                    onChange={(e) => set("email", e.target.value)}
                                                    placeholder={t("demo.form.emailPlaceholder") as string}
                                                    className="w-full px-3 py-2.5 text-sm border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary transition-colors"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-300 mb-1.5">{t("demo.form.company")}</label>
                                                <input
                                                    type="text"
                                                    value={form.company}
                                                    onChange={(e) => set("company", e.target.value)}
                                                    placeholder={t("demo.form.companyPlaceholder") as string}
                                                    className="w-full px-3 py-2.5 text-sm border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary transition-colors"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-300 mb-1.5">{t("demo.form.role")}</label>
                                                <input
                                                    type="text"
                                                    value={form.role}
                                                    onChange={(e) => set("role", e.target.value)}
                                                    placeholder={t("demo.form.rolePlaceholder") as string}
                                                    className="w-full px-3 py-2.5 text-sm border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary transition-colors"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-300 mb-1.5">{t("demo.form.teamSize")}</label>
                                                <select
                                                    value={form.teamSize}
                                                    onChange={(e) => set("teamSize", e.target.value)}
                                                    className="w-full px-3 py-2.5 text-sm border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary transition-colors"
                                                >
                                                    <option value="">{t("demo.form.teamSizePlaceholder")}</option>
                                                    {TEAM_SIZE_OPTIONS.map((s) => (
                                                        <option key={s} value={s}>{s}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-300 mb-1.5">{t("demo.form.monthlyRevenue")}</label>
                                                <select
                                                    value={form.monthlyRevenue}
                                                    onChange={(e) => set("monthlyRevenue", e.target.value)}
                                                    className="w-full px-3 py-2.5 text-sm border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary transition-colors"
                                                >
                                                    <option value="">{t("demo.form.monthlyRevenuePlaceholder")}</option>
                                                    {REVENUE_OPTIONS.map((r) => (
                                                        <option key={r} value={r}>{t(`demo.form.revenue.${r}`)}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-300 mb-2">{t("demo.form.channels")}</label>
                                            <div className="flex flex-wrap gap-2">
                                                {CHANNEL_OPTIONS.map((c) => {
                                                    const active = form.channels.includes(c);
                                                    return (
                                                        <button
                                                            type="button"
                                                            key={c}
                                                            onClick={() => toggleChannel(c)}
                                                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active
                                                                ? "bg-accent-primary text-white border-accent-primary"
                                                                : "bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 border-neutral-200 dark:border-neutral-700 hover:border-accent-primary/40"
                                                                }`}
                                                        >
                                                            {t(`channels.${c}`)}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-300 mb-2">{t("demo.form.slot")}</label>
                                            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                                                {slots.map((s) => {
                                                    const active = form.slot === s.date;
                                                    return (
                                                        <button
                                                            type="button"
                                                            key={s.date}
                                                            onClick={() => set("slot", s.date)}
                                                            className={`text-left px-3 py-2.5 rounded-lg border transition-colors ${active
                                                                ? "bg-accent-primary/5 border-accent-primary text-accent-primary"
                                                                : "bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 hover:border-accent-primary/40"
                                                                }`}
                                                        >
                                                            <span className="block text-xs font-semibold">{s.label}</span>
                                                            <span className="block text-[10px] text-neutral-500 dark:text-neutral-400 mt-0.5">{s.sub}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-2 flex items-center gap-1.5">
                                                <Clock size={12} />
                                                {t("demo.form.slotNote")}
                                            </p>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-300 mb-1.5">{t("demo.form.message")}</label>
                                            <textarea
                                                value={form.message}
                                                onChange={(e) => set("message", e.target.value)}
                                                rows={3}
                                                placeholder={t("demo.form.messagePlaceholder") as string}
                                                className="w-full px-3 py-2.5 text-sm border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary transition-colors resize-none"
                                            />
                                        </div>

                                        <div className="pt-2">
                                            {submitError && (
                                                <div
                                                    role="alert"
                                                    className="mb-3 px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-red-700 text-xs dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200"
                                                >
                                                    {submitError}
                                                </div>
                                            )}
                                            <button
                                                type="submit"
                                                disabled={!canSubmit || isSubmitting}
                                                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-accent-primary text-white rounded-lg text-sm font-semibold hover:bg-accent-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-accent-primary/20"
                                            >
                                                {isSubmitting ? t("demo.form.submitting") : t("demo.form.submit")}
                                                {!isSubmitting && <ArrowRight size={16} />}
                                            </button>
                                            <p className="mt-3 text-[11px] text-neutral-500 dark:text-neutral-400 text-center inline-flex items-center gap-1.5 w-full justify-center">
                                                <ShieldCheck size={12} />
                                                {t("demo.form.privacy")}
                                            </p>
                                        </div>
                                    </form>
                                )}
                            </motion.div>

                            {/* Benefits column */}
                            <motion.aside
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                                className="lg:col-span-2 space-y-4"
                            >
                                <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl p-6">
                                    <h3 className="text-sm font-semibold text-text-primary mb-4">{t("demo.benefits.title")}</h3>
                                    <ul className="space-y-3">
                                        {[
                                            { iconNode: <Clock size={16} />, key: "demo.benefits.b1" },
                                            { iconNode: <Sparkles size={16} />, key: "demo.benefits.b2" },
                                            { iconNode: <Users size={16} />, key: "demo.benefits.b3" },
                                            { iconNode: <MessageSquare size={16} />, key: "demo.benefits.b4" },
                                        ].map((b) => (
                                            <li key={b.key} className="flex items-start gap-3 text-sm text-text-primary">
                                                <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-lg bg-accent-primary/10 text-accent-primary shrink-0">
                                                    {b.iconNode}
                                                </span>
                                                <span className="text-neutral-700 dark:text-neutral-300 leading-relaxed">{t(b.key)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="bg-text-primary dark:bg-neutral-800 text-white rounded-2xl p-6 relative overflow-hidden">
                                    <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-accent-primary/30 blur-3xl" aria-hidden />
                                    <div className="relative">
                                        <Globe2 size={20} className="text-accent-primary mb-3" />
                                        <h3 className="text-sm font-semibold mb-2">{t("demo.languages.title")}</h3>
                                        <p className="text-xs text-neutral-300 leading-relaxed">{t("demo.languages.body")}</p>
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl p-6">
                                    <h3 className="text-sm font-semibold text-text-primary mb-2">{t("demo.startNow.title")}</h3>
                                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4 leading-relaxed">{t("demo.startNow.body")}</p>
                                    <button
                                        onClick={openRegister}
                                        className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 text-text-primary dark:text-neutral-100 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                                    >
                                        {t("landing.cta.startFree")}
                                        <ArrowRight size={14} />
                                    </button>
                                </div>
                            </motion.aside>
                        </div>
                    </div>
                </section>

                {/* Footer */}
                <footer className="bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800 py-10 mt-20">
                    <div className="container mx-auto px-4 sm:px-6 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-neutral-500 dark:text-neutral-400">
                        <div className="flex items-center gap-2">
                            <Logo className="w-5 h-5" />
                            <span className="text-lg font-medium font-display text-text-primary dark:text-neutral-100 tracking-tight">
                                {t("brandName")}
                            </span>
                        </div>
                        <div className="flex flex-wrap justify-center gap-5">
                            <a href="/terms" className="hover:text-text-primary transition-colors">
                                {t("legal.tos.title")}
                            </a>
                            <a href="/privacy" className="hover:text-text-primary transition-colors">
                                {t("legal.privacy.title")}
                            </a>
                            <CookieSettingsButton variant="text" />
                        </div>
                        <div>{t("landing.copyright")}</div>
                    </div>
                </footer>
            </main>
        </div>
    );
};

export default DemoPage;
